import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';

import { useMutation, UseMutationResult } from '@tanstack/react-query';

import { useHttpClient } from '@dotfile/frontend/shared/common';
import { useToast } from '@dotfile/frontend/shared/design-system';
import { FileModel } from '@dotfile/shared/domain';

import { useHandleError } from '../../error-handler';

// Define input types for the hook
export type SaveFileInput =
  | Pick<FileModel, 'id' | 'name'> // Standard file input with ID
  | { url: string; name: string }; // URL-based input

export type SaveFileMutation = UseMutationResult<void, unknown, SaveFileInput>;

export const useSaveFile = ({ showSuccessToast = false } = {}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const httpClient = useHttpClient();
  const handleError = useHandleError();

  const saveFile = useMutation({
    mutationFn: async (file: SaveFileInput) => {
      let blob: Blob;
      const fileName = file.name;

      // Check if the input has a URL property
      if ('url' in file) {
        // Direct URL download
        const response = await fetch(file.url);
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`);
        }
        blob = await response.blob();
      } else {
        // Standard file ID download
        const res = await httpClient.get(`/files/preview/${file.id}`, {
          responseType: 'blob',
        });
        blob = res.data;
      }

      saveAs(blob, fileName);

      if (showSuccessToast)
        toast({
          title: t('file.save.success', {
            defaultValue: 'File {{fileName}} downloaded',
            ns: 'components',
            fileName,
          }),
          description: fileName,
          status: 'success',
        });
    },
    onError: (error: Error, file) => {
      handleError({
        title: t('file.save.error', {
          defaultValue: 'Something went wrong when downloading {{fileName}}',
          ns: 'components',
          fileName: file.name,
        }),
        error,
      });
    },
  });

  return saveFile;
};
